/**
 * Pure logic of the product line dialog (port of the AddProductLineComponent
 * form): options, form values <-> record, validation, store tree rows and their
 * "no workshop" warnings.
 */
import type { Store } from '@/shared/config/app-config';
import { compareCodeUnits } from '@/shared/lib/compare';
import { getImage, STORAGE } from '@/shared/lib/image';
import type { ProductLine, ProductLineInput } from './api';

export const SAV_OPTIONS = [
  { label: 'NO', value: 'false' },
  { label: 'YES', value: 'true' },
] as const;

export const SIZE_DISPLAY_OPTIONS = [
  { label: 'No size (1 view)', value: 'noSize' },
  { label: 'Sizes (2 views)', value: 'size' },
  { label: 'Shoe sizes (3 views)', value: 'shoeSize' },
] as const;

export const SHADOW_OPTIONS = [
  { label: 'Thread Flat', value: 'flat' },
  { label: 'Shoes', value: 'shoes' },
  { label: 'Leather', value: 'leather' },
] as const;

export interface ProductLineFormValues {
  productLine: string;
  commercialProductLine: string;
  display: string;
  productGroup: string;
  sav: 'true' | 'false' | '';
  shadowType: string;
  sizeDisplay: string;
}

export type ProductLineFormErrors = Partial<
  Record<keyof ProductLineFormValues, string>
>;

export const emptyProductLineForm = (): ProductLineFormValues => ({
  productLine: '',
  commercialProductLine: '',
  display: '1',
  productGroup: '',
  sav: 'true',
  shadowType: 'flat',
  sizeDisplay: '',
});

/** Radio value of a stored boolean, unset when the value is missing. */
const booleanChoice = (
  value: boolean | null | undefined,
): ProductLineFormValues['sav'] => {
  if (value == null) return '';
  return value ? 'true' : 'false';
};

export const productLineToForm = (
  item: ProductLine,
): ProductLineFormValues => ({
  productLine: item.productLine ?? '',
  commercialProductLine: item.commercialProductLine ?? '',
  display: item.display == null ? '' : String(item.display),
  productGroup: item.productGroup ?? '',
  sav: booleanChoice(item.sav),
  shadowType: item.shadowType ?? '',
  sizeDisplay: item.sizeDisplay ?? '',
});

const DISPLAY_PATTERN = /^\d{1,5}$/;

export const validateProductLineForm = (
  values: ProductLineFormValues,
): ProductLineFormErrors => {
  const errors: ProductLineFormErrors = {};
  if (!values.productLine || values.productLine.length > 50)
    errors.productLine = 'Wrong product line';
  if (!values.commercialProductLine || values.commercialProductLine.length > 50)
    errors.commercialProductLine = 'Wrong commercial product line';
  if (values.display && !DISPLAY_PATTERN.test(values.display))
    errors.display = 'Wrong display';
  if (values.productGroup.length > 50)
    errors.productGroup = 'Wrong product group';
  if (values.sav === '') errors.sav = 'Wrong post purchase allow';
  return errors;
};

/** Initial translations table: the record's, or `{ EN: commercialProductLine }` for a record without any. */
export const initialTranslations = (
  item: ProductLine | null,
): Record<string, string> =>
  item
    ? {
        ...(item.commercialProductLineTranslations ?? {
          EN: item.commercialProductLine ?? '',
        }),
      }
    : {};

/** Payload of the create / update calls. */
export const formToProductLine = (
  values: ProductLineFormValues,
  stores: readonly string[],
  translations: Record<string, string>,
  now: number,
): ProductLineInput => ({
  productLine: values.productLine,
  commercialProductLine: values.commercialProductLine,
  display: values.display,
  productGroup: values.productGroup,
  sav: values.sav === 'true',
  shadowType: values.shadowType || null,
  sizeDisplay: values.sizeDisplay || null,
  stores: [...stores],
  commercialProductLineTranslations: translations,
  texturePictureUpdatedAt: now,
});

export interface StoreLink extends Record<string, unknown> {
  id: string;
  label: string;
  country: string | null | undefined;
  region: string | null | undefined;
}

/** Rows of the "Available in store" tree, sorted by name. */
export const storeLinks = (stores: readonly Store[]): StoreLink[] =>
  stores
    .map((store) => ({
      id: String(store.id),
      label: store.name,
      country: store.country,
      region: store.region,
    }))
    .sort((a, b) => compareCodeUnits(a.label, b.label));

const hasWorkshop = (store: Store) =>
  !!store.workshopEventId || !!store.workshopSavId;

/**
 * Warning of the store tree: a store (or a whole country) without any Event
 * or SAV workshop. Never shown for a product line tied to a workshop.
 */
export const storeWarning = (
  stores: readonly Store[],
  productLineHasWorkshop: boolean,
  node: { level: number; country?: string; storeId?: string },
): string | null => {
  if (node.level === 0 || productLineHasWorkshop) return null;
  if (node.level === 1) {
    const ofCountry = stores.filter((store) => store.country === node.country);
    return ofCountry.every((store) => !hasWorkshop(store))
      ? 'Stores without Event or SAV workshop'
      : null;
  }
  const store = stores.find((entry) => String(entry.id) === node.storeId);
  return store && !hasWorkshop(store)
    ? 'Store without Event or SAV workshop'
    : null;
};

/** Signed URL of a product line picture stored in the ABCDior back storage. */
export const productLinePictureUrl = (
  id: string | undefined,
  type: 'texture' | 'custom',
): string | null =>
  id
    ? getImage(
        'abc',
        STORAGE.AbcDiorBack,
        `/product-line-pictures/${id}-${type}`,
      )
    : null;
